/**
 * ToastContext.jsx
 *
 * Lightweight drop-in replacement for the `sonner` npm package.
 * Provides the same surface API used in the original project:
 *
 *   import { toast } from 'sonner';
 *   toast.error('...')
 *   toast.success('...')
 *
 * Usage:
 *   1. Wrap your app in <ToastProvider>
 *   2. Call useToast() in any child component to get the toast object
 */

import React, { createContext, useCallback, useContext, useState } from 'react';

const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const push = useCallback((msg, type = 'success') => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, msg, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3500);
  }, []);

  // Same shape as sonner's `toast` export
  const toast = {
    error:   (msg) => push(msg, 'error'),
    success: (msg) => push(msg, 'success'),
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div className="hx-toast-container">
        {toasts.map((t) => (
          <div key={t.id} className={`hx-toast ${t.type}`}>
            {t.msg}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/**
 * useToast()
 * Returns the toast object: { error(msg), success(msg) }
 * Must be used inside a <ToastProvider>.
 */
export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a <ToastProvider>');
  return ctx;
}
